/**
 * data/source — seleção da fonte de dados por área de domínio (DD-004, RNF 8.2, Req 26.2).
 *
 * Ponto único que decide, por área, se um repositório lê do mock tipado
 * (`data/mock.ts`, dev/preview) ou da API real `/v1` (produção e áreas promovidas).
 *
 * Tree-shaking (seção 6.7 do design): `data/mock.ts` NUNCA é importado
 * estaticamente; só via `import()` dinâmico no ramo de mock, atrás do guard
 * `import.meta.env.PROD`. Em build de produção a constante `PROD` é substituída
 * pelo Vite e o ramo de mock é eliminado por dead-code elimination — o chunk de
 * `mock.ts` não entra no bundle de produção.
 */

// `import type` é apagado em runtime (não gera import estático no bundle) —
// serve apenas para tipar o módulo de mock carregado por `import()` dinâmico.
import type * as MockModule from "./mock.js";

/** Áreas de domínio (1:1 com a seção 8 do design — contrato `/v1`). */
export const DATA_AREAS = [
  "session",
  "opportunities",
  "accounts",
  "accountTiers",
  "contacts",
  "partners",
  "activities",
  "inbox",
  "conversations",
  "goals",
  "reports",
  "branding",
  "businessUnits",
  "users",
  "roles",
  "tenants",
  "auditLogs",
  "imports",
  "digest",
  "copilot",
  "channels",
  "queues",
  "quickReplies",
  "tags",
  "recordings",
  "context",
  "workflows",
] as const;

export type DataArea = (typeof DATA_AREAS)[number];

const ALL_AREAS: ReadonlySet<DataArea> = new Set(DATA_AREAS);

function isKnownArea(token: string): token is DataArea {
  return ALL_AREAS.has(token as DataArea);
}

/**
 * Faz o parsing puro da flag `VITE_API_REAL_AREAS` em um `Set<DataArea>`.
 *
 * - `*` → todas as áreas são reais.
 * - lista separada por vírgula → apenas as áreas listadas e conhecidas.
 * - token desconhecido → ignorado (com `console.warn` em dev).
 * - vazio/whitespace → nenhuma área real.
 */
export function parseRealAreas(envValue: string | undefined): Set<DataArea> {
  const raw = (envValue ?? "").trim();
  if (raw === "") return new Set();
  if (raw === "*") return new Set(DATA_AREAS);

  const result = new Set<DataArea>();
  for (const token of raw.split(",")) {
    const name = token.trim();
    if (name === "") continue;
    if (isKnownArea(name)) {
      result.add(name);
    } else if (import.meta.env.DEV) {
      console.warn(`[source] área desconhecida ignorada em VITE_API_REAL_AREAS: "${name}"`);
    }
  }
  return result;
}

// Memoização por valor bruto: efetivamente "congelado" em produção (env é
// constante de build), mas recomputável em teste quando a env é stubada.
let cache: { raw: string; set: Set<DataArea> } | null = null;

function realAreas(): Set<DataArea> {
  const raw = import.meta.env["VITE_API_REAL_AREAS"] ?? "";
  if (cache === null || cache.raw !== raw) {
    cache = { raw, set: parseRealAreas(raw) };
  }
  return cache.set;
}

/** Seletor puro: a área lê da API real? Em produção, todas as áreas são reais. */
export function isAreaReal(area: DataArea): boolean {
  if (import.meta.env.PROD) return true;
  return realAreas().has(area);
}

export type SourceKind = "api" | "mock";

export interface DataSourceResolution {
  area: DataArea;
  kind: SourceKind;
  /** Módulo de mock — presente apenas em dev/preview quando a área usa mock. */
  mock?: typeof MockModule;
}

/**
 * Resolve a fonte de uma área. Em produção (ou área promovida) usa a API; caso
 * contrário, carrega o mock por `import()` dinâmico (code-split, tree-shaken
 * fora de produção). Nunca importa mock estaticamente.
 */
export async function resolveSource(area: DataArea): Promise<DataSourceResolution> {
  if (import.meta.env.PROD || isAreaReal(area)) {
    return { area, kind: "api" };
  }
  const mock = await import("./mock.js");
  return { area, kind: "mock", mock };
}
